import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  KeyboardEvent,
  FormEvent,
} from 'react';
import { MessageCircle, Send, Edit2, Trash2, X, Check, Pin, PinOff } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { formatDateTime } from '../../utils/dateUtils';

// Internal notes thread shared by the accounting detail pages. Mirrors the
// repairs InternalNotes component (client/src/components/repairs/InternalNotes.js).
// The caller supplies the API base path, so the same UI serves payments
// (`payment_comments`) and B2B orders (`order_comments`). Notes only — no
// attachments.

export interface InternalNote {
  id: number;
  comment_text: string;
  created_by: number | string;
  created_by_name?: string | null;
  created_at: string;
  is_pinned: boolean;
}

export interface InternalNotesThreadHandle {
  refresh: () => Promise<void>;
}

interface InternalNotesThreadProps {
  /** Id of the payment or order the notes belong to. Null disables the thread. */
  entityId: number | string | null | undefined;
  /** API path without the trailing `/comments`, e.g. `/api/accounting/payments/12`. */
  apiBase: string;
  /** Sentence shown under the empty-state heading. */
  emptyHint: string;
  onRefresh?: () => void;
}

const InternalNotesThread = forwardRef<InternalNotesThreadHandle, InternalNotesThreadProps>(
  ({ entityId, apiBase, emptyHint, onRefresh }, ref) => {
    const { user } = useAuth() as { user: { id: number | string } | null };
    const [comments, setComments] = useState<InternalNote[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [newComment, setNewComment] = useState('');
    const [sending, setSending] = useState(false);
    const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
    const [editingText, setEditingText] = useState('');
    const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);

    const fetchComments = useCallback(async () => {
      try {
        setLoading(true);

        if (!entityId) {
          setComments([]);
          setLoading(false);
          return;
        }

        const response = await axios.get<InternalNote[]>(`${apiBase}/comments`);

        // Sort: pinned first, then newest first within each group.
        const sortedComments = (response.data || []).slice().sort((a, b) => {
          if (a.is_pinned && !b.is_pinned) return -1;
          if (!a.is_pinned && b.is_pinned) return 1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });

        setComments(sortedComments);
        setError('');
      } catch (err) {
        console.error('Error fetching internal notes:', err);
        setError('Failed to load internal notes');
      } finally {
        setLoading(false);
      }
    }, [entityId, apiBase]);

    useEffect(() => {
      if (entityId) {
        fetchComments();
      }
    }, [entityId, fetchComments]);

    useImperativeHandle(
      ref,
      () => ({
        refresh: fetchComments,
      }),
      [fetchComments]
    );

    const handleSubmitComment = async (e: FormEvent) => {
      e.preventDefault();

      if (!newComment.trim() || !user || !entityId) {
        return;
      }

      try {
        setSending(true);
        await axios.post(`${apiBase}/comments`, {
          comment_text: newComment.trim(),
          created_by: user.id,
        });

        setNewComment('');
        await fetchComments();
        setError('');

        if (onRefresh) {
          onRefresh();
        }
      } catch (err) {
        console.error('Error adding internal note:', err);
        setError('Failed to add internal note');
      } finally {
        setSending(false);
      }
    };

    const handleStartEdit = (comment: InternalNote) => {
      setEditingCommentId(comment.id);
      setEditingText(comment.comment_text);
    };

    const handleCancelEdit = () => {
      setEditingCommentId(null);
      setEditingText('');
    };

    const handleSaveEdit = async (commentId: number) => {
      if (!editingText.trim() || !user || !entityId) {
        return;
      }

      try {
        await axios.put(`${apiBase}/comments/${commentId}`, {
          comment_text: editingText.trim(),
          user_id: user.id,
        });

        setEditingCommentId(null);
        setEditingText('');
        await fetchComments();
        setError('');

        if (onRefresh) {
          onRefresh();
        }
      } catch (err: any) {
        console.error('Error updating internal note:', err);
        setError(err?.response?.data?.error || 'Failed to update internal note');
      }
    };

    const handleDelete = async (commentId: number) => {
      if (!user || !entityId) {
        return;
      }

      if (!window.confirm('Are you sure you want to delete this internal note?')) {
        return;
      }

      try {
        setDeletingCommentId(commentId);
        await axios.delete(`${apiBase}/comments/${commentId}`, {
          params: { user_id: user.id },
        });

        await fetchComments();
        setError('');

        if (onRefresh) {
          onRefresh();
        }
      } catch (err: any) {
        console.error('Error deleting internal note:', err);
        setError(err?.response?.data?.error || 'Failed to delete internal note');
      } finally {
        setDeletingCommentId(null);
      }
    };

    const handleTogglePin = async (commentId: number) => {
      if (!entityId) return;

      try {
        await axios.patch(`${apiBase}/comments/${commentId}/pin`);
        await fetchComments();
        setError('');

        if (onRefresh) {
          onRefresh();
        }
      } catch (err: any) {
        console.error('Error toggling pin on internal note:', err);
        setError(err?.response?.data?.error || 'Failed to update pin status');
      }
    };

    if (loading) {
      return (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Internal Notes</h3>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading internal notes...</span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Internal Notes</h3>
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Internal Notes</h3>

        {/* Add Comment Form */}
        {entityId && (
          <form onSubmit={handleSubmitComment} className="mb-4">
            <div className="relative">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add an internal note..."
                rows={1}
                className="w-full px-3 py-2 pr-12 border-2 border-gray-200 rounded-lg shadow-sm placeholder-gray-400
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-all duration-200 resize-none text-sm"
                disabled={sending}
                onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmitComment(e);
                  }
                }}
              />
              <button
                type="submit"
                disabled={!newComment.trim() || sending}
                className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8
                         rounded-full text-white bg-naviter-blue hover:bg-naviter-blue-dark
                         focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-naviter-blue
                         disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-naviter-blue
                         transition-all duration-200 shadow-sm hover:shadow-md p-0"
                title="Send note (Enter)"
              >
                {sending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                ) : (
                  <Send className="h-4 w-4" style={{ marginLeft: '-1px', marginTop: '1px' }} />
                )}
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
              <span className="flex items-center">
                <span className="inline-block w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                Internal only - not visible to customer
              </span>
              <span className="text-gray-400">
                <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs font-mono">Enter</kbd> to send
              </span>
            </div>
          </form>
        )}

        {comments.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No internal notes yet</h3>
            <p className="mt-1 text-sm text-gray-500">{emptyHint}</p>
          </div>
        ) : (
          <div className="relative">
            {/* Vertical timeline line - centered on the dots */}
            <div className="absolute left-[11px] top-0 bottom-0 w-0.5 bg-gray-300"></div>

            <div className="space-y-3">
              {comments.map((comment, index) => {
                // PostgreSQL BIGINT returns as string; compare as strings.
                const isOwnComment = !!user && String(comment.created_by) === String(user.id);
                const isEditing = editingCommentId === comment.id;
                const isDeleting = deletingCommentId === comment.id;
                const isPinned = comment.is_pinned;

                return (
                  <div key={comment.id || index} className="relative flex items-start">
                    <div className={`relative z-10 flex items-center justify-center w-6 h-6 rounded-full border-2 border-white ${isPinned ? 'bg-amber-500' : 'bg-blue-400'}`}>
                      {isPinned ? (
                        <Pin className="h-3 w-3 text-white" />
                      ) : (
                        <MessageCircle className="h-3 w-3 text-white" />
                      )}
                    </div>

                    <div className="ml-3 min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-900">
                          <span className="font-medium text-gray-500">
                            {formatDateTime(comment.created_at)}
                          </span>
                          <span className="mx-1 text-gray-400">•</span>
                          <span className="font-medium text-gray-900">
                            {comment.created_by_name || 'Unknown'}
                          </span>
                          {isPinned && (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                              Pinned
                            </span>
                          )}
                        </div>

                        {!isEditing && (
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => handleTogglePin(comment.id)}
                              className={`p-0.5 transition-colors ${isPinned ? 'text-amber-500 hover:text-amber-700' : 'text-gray-400 hover:text-amber-500'}`}
                              title={isPinned ? 'Unpin note' : 'Pin note'}
                            >
                              {isPinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                            </button>

                            {isOwnComment && (
                              <>
                                <button
                                  onClick={() => handleStartEdit(comment)}
                                  className="p-0.5 text-gray-400 hover:text-naviter-blue transition-colors"
                                  title="Edit note"
                                  disabled={isDeleting}
                                >
                                  <Edit2 className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={() => handleDelete(comment.id)}
                                  className="p-0.5 text-gray-400 hover:text-red-600 transition-colors"
                                  title="Delete note"
                                  disabled={isDeleting}
                                >
                                  {isDeleting ? (
                                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-red-600 border-t-transparent"></div>
                                  ) : (
                                    <Trash2 className="h-3 w-3" />
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>

                      {isEditing ? (
                        <div className="mt-1">
                          <textarea
                            value={editingText}
                            onChange={(e) => setEditingText(e.target.value)}
                            rows={3}
                            className="w-full px-2 py-1 border-2 border-naviter-blue/30 rounded-md shadow-sm
                                     focus:outline-none focus:ring-2 focus:ring-naviter-blue focus:border-transparent
                                     resize-none text-sm"
                            autoFocus
                            onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
                              if (e.key === 'Escape') {
                                handleCancelEdit();
                              }
                            }}
                          />
                          <div className="mt-1 flex items-center justify-end space-x-2">
                            <button
                              onClick={handleCancelEdit}
                              className="px-2 py-1 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200
                                       rounded-md transition-colors flex items-center space-x-1"
                            >
                              <X className="h-3 w-3" />
                              <span>Cancel</span>
                            </button>
                            <button
                              onClick={() => handleSaveEdit(comment.id)}
                              disabled={!editingText.trim()}
                              className="px-2 py-1 text-xs text-white bg-naviter-blue hover:bg-naviter-blue-dark
                                       rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-naviter-blue
                                       flex items-center space-x-1"
                            >
                              <Check className="h-3 w-3" />
                              <span>Save</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className={`mt-1 p-2 rounded-md border-l-2 ${isPinned ? 'bg-amber-50 border-amber-400' : 'bg-gray-50 border-blue-200'}`}>
                          <p className={`text-sm whitespace-pre-wrap leading-snug ${isPinned ? 'text-amber-900' : 'text-gray-700'}`}>
                            {comment.comment_text}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }
);

InternalNotesThread.displayName = 'InternalNotesThread';

export default InternalNotesThread;
